/**
 * pricing.ts — currency detection + price table
 *
 * Detects whether the user is likely in Brazil and selects BRL or USD accordingly.
 * Detection heuristic (no external request needed):
 *   1. Explicit override stored in localStorage ("currency" key: "BRL" | "USD")
 *   2. navigator.language === "pt-BR"
 *   3. Intl.DateTimeFormat resolved timezone starts with "America/Sao_Paulo"
 *      or equals "America/Manaus", "America/Fortaleza", "America/Recife",
 *      "America/Belem", "America/Maceio", "America/Cuiaba", "America/Porto_Velho",
 *      "America/Boa_Vista", "America/Rio_Branco", "America/Noronha"
 *
 * The hook `useCurrency()` exposes state + toggle that persists to localStorage.
 */

import { useCallback } from 'react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type Currency = 'USD' | 'BRL';

export interface PriceEntry {
  monthly: number;
  yearly: number; // total for the year
}

export interface AddonPrices {
  usd: PriceEntry;
  brl: PriceEntry;
}

// ---------------------------------------------------------------------------
// Price table — single source of truth
// ---------------------------------------------------------------------------

// Single standard price table (shown in R$). The `usd` entry is the one read by
// useCurrency; we keep the same numbers in `brl` for clarity. Régua: Bundle
// "Tudo" ≈ R$99,90/mês; add-ons avulsos menores. Anual ≈ 2 meses grátis.
export const ADDON_PRICES: Record<string, AddonPrices> = {
  'ia-studio': {
    usd: { monthly: 59.90, yearly: 599.00 },
    brl: { monthly: 59.90, yearly: 599.00 },
  },
  craft: {
    usd: { monthly: 29.90, yearly: 299.00 },
    brl: { monthly: 29.90, yearly: 299.00 },
  },
  outlining: {
    usd: { monthly: 39.90, yearly: 399.00 },
    brl: { monthly: 39.90, yearly: 399.00 },
  },
  bundle: {
    usd: { monthly: 99.90, yearly: 999.00 },
    brl: { monthly: 99.90, yearly: 999.00 },
  },
};

export const COVER_CREDITS_PRICE: Record<Currency, number> = {
  USD: 19.90,
  BRL: 19.90,
};

// ---------------------------------------------------------------------------
// Formatter
// ---------------------------------------------------------------------------

export function formatPrice(amount: number, currency: Currency): string {
  const locale = currency === 'BRL' ? 'pt-BR' : 'en-US';
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

/** Returns the per-month price from a yearly total */
export function yearlyPerMonth(yearlyTotal: number): number {
  return yearlyTotal / 12;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export interface UseCurrencyReturn {
  currency: Currency;
  toggle: () => void;
  /** Format a price value for the current currency */
  fmt: (amount: number) => string;
  /** Get monthly price for an addon in the current currency */
  monthly: (addonId: string) => number;
  /** Get yearly total price for an addon in the current currency */
  yearly: (addonId: string) => number;
  /** Get per-month equivalent of the yearly price */
  yearlyMonthly: (addonId: string) => number;
  /** Cover credits price for the current currency */
  coverCredits: number;
}

export function useCurrency(): UseCurrencyReturn {
  // SINGLE STANDARD PRICE for everyone — no locale differentiation.
  // We use the (lower) USD price *values* and present them in BRL, since billing
  // runs through Asaas (which charges in Reais). There is no currency toggle:
  // "o mesmo valor que está em dólares é para o Brasil, sem diferenciação".
  const currency: Currency = 'BRL';

  const toggle = useCallback(() => {
    /* no-op — single standard price, no currency switching */
  }, []);

  const fmt = useCallback((amount: number) => formatPrice(amount, 'BRL'), []);

  const monthly = useCallback(
    (addonId: string) => ADDON_PRICES[addonId]?.usd.monthly ?? 0,
    []
  );

  const yearly = useCallback(
    (addonId: string) => ADDON_PRICES[addonId]?.usd.yearly ?? 0,
    []
  );

  const yearlyMonthly = useCallback(
    (addonId: string) => yearlyPerMonth(yearly(addonId)),
    [yearly]
  );

  const coverCredits = COVER_CREDITS_PRICE.USD;

  return { currency, toggle, fmt, monthly, yearly, yearlyMonthly, coverCredits };
}
